import {
  AppRoleEnum,
  CompanyContractPlan,
  MeAppRoleEnum,
  MeCompanyContractPlanEnum,
  PlaceNodeFromJSON,
} from '@ulysses-inc/harami_api_client'
import { ExhaustiveError } from '~/utils/error'
import type {
  GetMeResponse,
  PlaceNode,
  PrincipalTypeEnum,
} from '@ulysses-inc/harami_api_client'

/**
 * 認証主体 (Principal) のユーザー固有情報。
 * User 認証時のみ存在し、キオスク端末アカウント (Device) 認証時は null になる。
 */
export type MePrincipalUser = {
  uuid: string
  role: AppRoleEnum
}

/**
 * 認証主体 (Principal) の情報。
 */
export type MePrincipal = {
  type: PrincipalTypeEnum
  /** 認証主体の KRN (Kaminashi Resource Name)。User なら members、Device なら devices 形式 */
  krn: string
  /** 表示名。User なら user.name、Device なら キオスク端末名 */
  name: string
  /** User 認証時のみ存在。Device 認証時は null */
  user: MePrincipalUser | null
}

export type MeCompany = {
  id: number
  name: string
  // GET /me のレスポンス (GetMeResponseCompany.contractPlan) は必須のため非オプショナル。
  contractPlan: CompanyContractPlan
}

/**
 * 認証主体自身の情報を表すドメインモデル。
 * GET /me のレスポンス (GetMeResponse) を変換して利用する (アンチコラプションレイヤー)。
 */
export type Me = {
  principal: MePrincipal
  company: MeCompany
  isLobsterEnabled: boolean
  placeNodes: PlaceNode[]
}

// MeAppRoleEnum / MeCompanyContractPlanEnum は、openapi-generator の制約で /me 専用に複製生成された
// enum であり、値は AppRoleEnum / CompanyContractPlan と一致する。
// ただし将来どちらか一方だけが変更されると `as` キャストでは番号ズレで別の値に化けてしまうため、
// 名前ベースで明示的にマッピングする。メンバーが追加された場合は ExhaustiveError でコンパイルエラーになる。
const toAppRole = (role: MeAppRoleEnum): AppRoleEnum => {
  switch (role) {
    case MeAppRoleEnum.OWNER:
      return AppRoleEnum.OWNER
    case MeAppRoleEnum.EDITOR:
      return AppRoleEnum.EDITOR
    case MeAppRoleEnum.LEADER:
      return AppRoleEnum.LEADER
    case MeAppRoleEnum.REPORTER:
      return AppRoleEnum.REPORTER
    default:
      throw new ExhaustiveError(role)
  }
}

const toContractPlan = (
  plan: MeCompanyContractPlanEnum,
): CompanyContractPlan => {
  switch (plan) {
    case MeCompanyContractPlanEnum.LIGHT:
      return CompanyContractPlan.LIGHT
    case MeCompanyContractPlanEnum.BASIC:
      return CompanyContractPlan.BASIC
    case MeCompanyContractPlanEnum.PREMIUM:
      return CompanyContractPlan.PREMIUM
    case MeCompanyContractPlanEnum.OLD_BASIC:
      return CompanyContractPlan.OLD_BASIC
    default:
      throw new ExhaustiveError(plan)
  }
}

/**
 * GET /me のレスポンスをドメインモデル Me に変換する。
 *
 * @param res GET /me のレスポンス
 * @returns ドメインモデル Me
 */
const fromResponse = (res: GetMeResponse): Me => {
  const { principal, company } = res

  return {
    principal: {
      type: principal.type,
      krn: principal.krn,
      name: principal.name,
      user: principal.user
        ? {
            uuid: principal.user.uuid,
            role: toAppRole(principal.user.role),
          }
        : null,
    },
    company: {
      id: company.id,
      name: company.name,
      contractPlan: toContractPlan(company.contractPlan),
    },
    isLobsterEnabled: res.isLobsterEnabled,
    // GetMeResponse.placeNodes の型は openapi-generator が複製生成した PlaceNode2
    // (PlaceNode と構造同一で nested enum の重複のみが差分。MeAppRoleEnum 等と同じ事情の現行生成物)。
    // blind cast を避け、生成元の公式デシリアライザで正規の PlaceNode へ変換する。
    placeNodes: res.placeNodes.map(PlaceNodeFromJSON),
  }
}

export const Me = {
  fromResponse,
}
